import { apiLoadChatContext, apiUpsertChatContext } from '../../../api/chatGoalState';
import { createStageResult } from './contracts';

// Hydrates persisted goal state for a chat document when stage persistence is enabled.
export const executeHydrateStage = async ({
  chatDocId = 0,
  enabled = false,
  loadChatContext = apiLoadChatContext
} = {}) => {
  if (!enabled || Number(chatDocId || 0) <= 0) {
    return {
      goalState: null,
      hydrateResult: createStageResult({
        stage: 'hydrate',
        status: 'skipped',
        decisionCode: 'hydrate_disabled',
        payload: {
          goalStateEnabled: false,
          hydrateStatus: 'skipped',
          hydrateHit: false,
          hydratedGoalUpdatedAt: null,
          hydrateError: null
        }
      })
    };
  }

  try {
    const persisted = await loadChatContext({ docId: Number(chatDocId || 0) });
    const goalState = persisted?.goalState && typeof persisted.goalState === 'object'
      ? persisted.goalState
      : null;

    return {
      goalState,
      hydrateResult: createStageResult({
        stage: 'hydrate',
        status: 'ok',
        decisionCode: goalState ? 'hydrate_db_hit' : 'hydrate_db_miss',
        payload: {
          goalStateEnabled: true,
          hydrateStatus: 'success',
          hydrateHit: Boolean(goalState),
          hydratedGoalUpdatedAt: persisted?.updatedAt || null,
          hydrateError: null
        }
      })
    };
  } catch (error) {
    return {
      goalState: null,
      hydrateResult: createStageResult({
        stage: 'hydrate',
        status: 'failed',
        decisionCode: 'hydrate_failed',
        reasonCodes: ['hydrate_error'],
        payload: {
          goalStateEnabled: true,
          hydrateStatus: 'failed',
          hydrateHit: false,
          hydratedGoalUpdatedAt: null,
          hydrateError: String(error?.message || 'hydrate failed')
        }
      })
    };
  }
};

// Runs hydrate stage, applies hydrated goal state, and emits hydrate telemetry payload.
export const executeHydrateAndEmitStage = async ({
  enabled = false,
  chatDocId = 0,
  goalState = null,
  goalStateHydrated = false,
  emitStage,
  normalizeGoalState,
  loadChatContext = apiLoadChatContext
} = {}) => {
  let nextGoalState = goalState;
  let nextGoalStateHydrated = Boolean(goalStateHydrated);

  const hydrateStage = enabled
    ? await executeHydrateStage({
      chatDocId,
      enabled: true,
      loadChatContext
    })
    : {
      goalState: null,
      hydrateResult: createStageResult({
        stage: 'hydrate',
        status: 'skipped',
        decisionCode: 'hydrate_disabled',
        payload: {
          goalStateEnabled: false,
          hydrateStatus: 'skipped',
          hydrateHit: false,
          hydratedGoalUpdatedAt: null,
          hydrateError: null
        }
      })
    };

  if (hydrateStage?.goalState && typeof hydrateStage.goalState === 'object') {
    nextGoalState = typeof normalizeGoalState === 'function'
      ? normalizeGoalState(hydrateStage.goalState)
      : hydrateStage.goalState;
    nextGoalStateHydrated = true;
  }

  const hydrateRecord = {
    status: hydrateStage?.hydrateResult?.payload?.hydrateStatus || (enabled ? 'failed' : 'skipped'),
    hit: Boolean(hydrateStage?.hydrateResult?.payload?.hydrateHit),
    updatedAt: hydrateStage?.hydrateResult?.payload?.hydratedGoalUpdatedAt || null,
    error: hydrateStage?.hydrateResult?.payload?.hydrateError || null
  };

  if (typeof emitStage === 'function') {
    emitStage('hydrate', {
      goalStateEnabled: Boolean(enabled),
      hydrateStatus: hydrateRecord.status,
      hydrateHit: Boolean(hydrateRecord.hit),
      hydratedGoalUpdatedAt: hydrateRecord.updatedAt,
      hydrateError: hydrateRecord.error
    });
  }

  return {
    goalState: nextGoalState,
    goalStateHydrated: nextGoalStateHydrated,
    hydrate: hydrateRecord
  };
};

// Persists goal state updates for the current request when persistence is enabled.
export const executePersistStage = async ({
  enabled = false,
  chatDocId = 0,
  requestId = null,
  goalState = null,
  upsertChatContext = apiUpsertChatContext
} = {}) => {
  if (!enabled || Number(chatDocId || 0) <= 0) {
    return createStageResult({
      stage: 'persist',
      status: 'skipped',
      decisionCode: 'persist_disabled',
      payload: {
        persistStatus: 'skipped',
        requestLogStatus: 'skipped',
        requestLogId: null
      }
    });
  }

  try {
    const persisted = await upsertChatContext({
      docId: Number(chatDocId || 0),
      requestId: requestId ? String(requestId) : null,
      goalState
    });

    return createStageResult({
      stage: 'persist',
      status: 'ok',
      decisionCode: 'persist_db_success',
      payload: {
        persistStatus: 'success',
        persistedGoalUpdatedAt: persisted?.updatedAt || null,
        persistError: null,
        requestLogStatus: 'pending',
        requestLogId: null
      }
    });
  } catch (error) {
    return createStageResult({
      stage: 'persist',
      status: 'failed',
      decisionCode: 'persist_db_failed',
      reasonCodes: ['persist_error'],
      payload: {
        persistStatus: 'failed',
        requestLogStatus: 'failed',
        requestLogId: null,
        persistError: String(error?.message || 'persist failed')
      }
    });
  }
};

// Builds the persist stage event payload from persist and request-log audit state.
export const createPersistStagePayload = ({
  persistResult = null,
  goalStateEnabled = false,
  requestLogAudit = null,
  goalStateSummary = null
} = {}) => {
  const payload = persistResult?.payload && typeof persistResult.payload === 'object'
    ? persistResult.payload
    : {};

  return {
    status: persistResult?.status || 'unknown',
    decisionCode: persistResult?.decisionCode || 'persist_unknown',
    goalStateEnabled: Boolean(goalStateEnabled),
    persistStatus: payload.persistStatus || 'skipped',
    persistedGoalUpdatedAt: payload.persistedGoalUpdatedAt || null,
    persistError: payload.persistError || null,
    requestLogStatus: requestLogAudit?.status || 'skipped',
    requestLogId: requestLogAudit?.id || null,
    requestLogError: requestLogAudit?.error || null,
    goalState: goalStateSummary && typeof goalStateSummary === 'object' ? goalStateSummary : null
  };
};

// Normalizes persisted goal-state status for trace/context audit records.
export const createPersistStateRecord = ({
  persistResult = null
} = {}) => {
  const payload = persistResult?.payload && typeof persistResult.payload === 'object'
    ? persistResult.payload
    : {};

  return {
    status: payload.persistStatus || 'skipped',
    updatedAt: payload.persistedGoalUpdatedAt || null,
    error: payload.persistError || null
  };
};

// Runs persist stage execution and emits normalized persist stage telemetry.
export const executePersistAndEmitStage = async ({
  enabled = false,
  chatDocId = 0,
  requestId = null,
  goalState = null,
  requestLogAudit = null,
  goalStatePersistence = null,
  summarizeGoalState,
  emitStage,
  onWarn,
  upsertChatContext = apiUpsertChatContext
} = {}) => {
  const persistStageResult = await executePersistStage({
    enabled,
    chatDocId,
    requestId,
    goalState,
    upsertChatContext
  });

  const nextGoalStatePersistence = {
    ...(goalStatePersistence && typeof goalStatePersistence === 'object' ? goalStatePersistence : {}),
    persist: createPersistStateRecord({ persistResult: persistStageResult })
  };

  if (String(persistStageResult?.status || '') === 'failed' && nextGoalStatePersistence?.persist?.error) {
    if (typeof onWarn === 'function') {
      onWarn(nextGoalStatePersistence.persist.error);
    }
  }

  if (typeof emitStage === 'function') {
    emitStage('persist', createPersistStagePayload({
      persistResult: persistStageResult,
      goalStateEnabled: enabled,
      requestLogAudit,
      goalStateSummary: typeof summarizeGoalState === 'function'
        ? summarizeGoalState(goalState)
        : null
    }));
  }

  return {
    persistStageResult,
    goalStatePersistence: nextGoalStatePersistence
  };
};
